// src/contexts/ThemeContext.jsx
// ✅ V2 UNIFICADO — light | dark | system · Única fuente de verdad

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const getSystemTheme = () => {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
};

const resolveTheme = (preference) => {
  if (preference === "system") return getSystemTheme();
  return preference === "dark" ? "dark" : "light";
};

export const ThemeProvider = ({ children }) => {
  // Preferencia: 'light' | 'dark' | 'system'
  const [theme, setThemeState] = useState(() => {
    try {
      const saved = localStorage.getItem("theme");
      if (saved === "dark" || saved === "light" || saved === "system") return saved;
      // Compat: theme_preference de Settings
      const pref = localStorage.getItem("theme_preference");
      if (pref === "dark" || pref === "light" || pref === "system") return pref;
    } catch (e) {
      // localStorage no disponible
    }
    return "light";
  });

  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(
    (() => {
      try {
        const saved = localStorage.getItem("theme");
        if (saved === "dark" || saved === "light" || saved === "system") return saved;
      } catch {}
      return "light";
    })()
  ));

  const [highContrast, setHighContrast] = useState(() => {
    try {
      return localStorage.getItem("highContrast") === "true";
    } catch {
      return false;
    }
  });

  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  });

  const setTheme = useCallback((next) => {
    const value = next === "dark" || next === "system" ? next : "light";
    setThemeState(value);
    try {
      localStorage.setItem("theme", value);
      localStorage.setItem("theme_preference", value);
    } catch {}
  }, []);

  // Accesibilidad del sistema
  useEffect(() => {
    let contrastQuery;
    let motionQuery;
    try {
      contrastQuery = window.matchMedia("(prefers-contrast: more)");
      motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    } catch {
      return;
    }

    const contrastHandler = (e) => {
      try {
        if (!localStorage.getItem("highContrast")) setHighContrast(e.matches);
      } catch {}
    };
    const motionHandler = (e) => setPrefersReducedMotion(e.matches);

    contrastQuery.addEventListener("change", contrastHandler);
    motionQuery.addEventListener("change", motionHandler);
    try {
      if (!localStorage.getItem("highContrast")) setHighContrast(contrastQuery.matches);
    } catch {}

    return () => {
      contrastQuery.removeEventListener("change", contrastHandler);
      motionQuery.removeEventListener("change", motionHandler);
    };
  }, []);

  // Resolver system + aplicar al DOM
  useEffect(() => {
    const apply = () => {
      const active = resolveTheme(theme);
      setResolvedTheme(active);
      const root = document.documentElement;

      if (active === "dark") {
        root.classList.add("dark");
        root.setAttribute("data-theme", "dark");
      } else {
        root.classList.remove("dark");
        root.setAttribute("data-theme", "light");
      }

      root.style.colorScheme = active;

      if (highContrast) root.classList.add("high-contrast");
      else root.classList.remove("high-contrast");

      if (prefersReducedMotion) {
        root.classList.add("reduce-motion");
        if (!document.getElementById("reduce-motion-styles")) {
          const style = document.createElement("style");
          style.id = "reduce-motion-styles";
          style.innerHTML =
            "*, *::before, *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }";
          document.head.appendChild(style);
        }
      } else {
        const style = document.getElementById("reduce-motion-styles");
        if (style) style.remove();
        root.classList.remove("reduce-motion");
      }

      try {
        localStorage.setItem("theme", theme);
        localStorage.setItem("theme_preference", theme);
        localStorage.setItem("highContrast", String(highContrast));
      } catch {}

      window.dispatchEvent(
        new CustomEvent("themeChanged", {
          detail: {
            theme,
            activeTheme: active,
            resolvedTheme: active,
            highContrast,
            prefersReducedMotion,
          },
        })
      );
    };

    apply();

    if (theme !== "system") return undefined;

    let mq;
    try {
      mq = window.matchMedia("(prefers-color-scheme: dark)");
    } catch {
      return undefined;
    }
    const onChange = () => apply();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme, highContrast, prefersReducedMotion]);

  const isDark = resolvedTheme === "dark";
  const isLight = resolvedTheme === "light";

  const toggleTheme = useCallback(() => {
    setTheme(isDark ? "light" : "dark");
  }, [isDark, setTheme]);

  const toggleHighContrast = useCallback(() => {
    setHighContrast((prev) => !prev);
  }, []);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const [orientation, setOrientation] = useState(
    typeof window !== "undefined" && window.innerHeight > window.innerWidth
      ? "portrait"
      : "landscape"
  );
  useEffect(() => {
    const handleOrientation = () => {
      setOrientation(window.innerHeight > window.innerWidth ? "portrait" : "landscape");
    };
    window.addEventListener("resize", handleOrientation);
    return () => window.removeEventListener("resize", handleOrientation);
  }, []);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      resolvedTheme,
      activeTheme: resolvedTheme,
      isDark,
      isLight,
      isHighContrast: highContrast,
      highContrast,
      setHighContrast,
      prefersReducedMotion,
      toggleTheme,
      toggleHighContrast,
      isMobile,
      orientation,
      resetPreferences: () => {
        setTheme("light");
        setHighContrast(false);
        try {
          localStorage.setItem("theme", "light");
          localStorage.setItem("theme_preference", "light");
          localStorage.setItem("highContrast", "false");
        } catch {}
      },
    }),
    [
      theme,
      setTheme,
      resolvedTheme,
      isDark,
      isLight,
      highContrast,
      prefersReducedMotion,
      toggleTheme,
      toggleHighContrast,
      isMobile,
      orientation,
    ]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
